import { DatePicker, Flex, Form, InputNumber } from "antd";
import { useForm } from "antd/es/form/Form";
import to from "await-to-js";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MandatoryField } from "../../../../../../components/MandatoryField/MandatoryField";
import { Modal } from "../../../../../../components/Modal/Modal";
import { ModalsContext } from "../../../../../../context/modals.context";
import { DATE_FORMAT } from "../../../../../../core/constants";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { usedStampsService } from "../../../../../../modules/obligee/usedStamps/usedStamps.service";
import { StavljanjeMarkicaUPrometPayload } from "../../../../../../modules/obligee/usedStamps/usedStamps.types";
import { clearNonNumericValues } from "../../../../../../utils/functions";
import style from "./UsedStampsPrometModal.module.scss";

export interface IUsedStampsPrometModalProps {
  getUsedStamps: () => Promise<void>;
}

export const UsedStampsPrometModal: FC<IUsedStampsPrometModalProps> = ({
  getUsedStamps,
}) => {
  const { isModalVisible, clearModals, setError, modals } =
    useContext(ModalsContext);
  const { t } = useTranslation();
  const [form] = useForm();

  const handleOk = () => {
    form.submit();
  };

  const handleClose = () => {
    form.resetFields();
    clearModals();
  };

  const handleSubmit = async (values: StavljanjeMarkicaUPrometPayload) => {
    const [err, res] = await to(
      usedStampsService.postStavljanjeMarkicaUPromet({
        datumStavljanjaUPromet: values.datumStavljanjaUPromet,
        iskoriscenOpsegId: modals.iskoriscenOpsegId,
        stavljanjeUPromet: true,
        kolicina: values?.kolicina,
        ...(showAmabalaza ? { ambalaza: values?.ambalaza } : {}),
      })
    );
    if (err || !res.success) {
      handleClose();
      return setError(res?.errorMessage);
    }
    getUsedStamps();
    handleClose();
  };

  const isBtnDisabled = useMemo(() => {
    return form.getFieldValue("datumStavljanjaUPromet");
  }, [form]);

  const getModalTitle = () => {
    switch (modals.markicaKategorijeId) {
      case AkciznaMarkicaKategorijaProzivoda.KAFA:
        return t("EVIDENCIJA_MARKICA.EVIDENTIRANJE_OPSEGA_MARKICA_KAFA");
      case AkciznaMarkicaKategorijaProzivoda.ALKOHOL:
        return t("EVIDENCIJA_MARKICA.EVIDENTIRANJE_OPSEGA_MARKICA_ALKOHOL");
      case AkciznaMarkicaKategorijaProzivoda.DUVAN:
        return t("EVIDENCIJA_MARKICA.EVIDENTIRANJE_OPSEGA_MARKICA_DUVAN");
      default:
        return t("EVIDENCIJA_MARKICA.STAVLJANJE_MARKICA_U_PROMET");
    }
  };

  const showAmabalaza = useMemo(() => {
    return (
      modals.markicaKategorijeId &&
      [
        AkciznaMarkicaKategorijaProzivoda.KAFA,
        AkciznaMarkicaKategorijaProzivoda.ALKOHOL,
      ].includes(modals.markicaKategorijeId)
    );
  }, [modals?.markicaKategorijeId]);

  return (
    <Modal
      name="company-user-modal"
      title={getModalTitle()}
      onOk={handleOk}
      cancelText={t("SETTINGS_COMPANY.NAZAD")}
      cancelButtonProps={{ type: "default" }}
      okText={t("SETTINGS_COMPANY.EVIDENTIRAJ")}
      okButtonProps={{ disabled: isBtnDisabled }}
      onCancel={handleClose}
      width={700}
      styles={{
        header: {
          padding: "0 24px 0px 0px",
        },
      }}
      open={isModalVisible("stavljanjeMarkicaUPromet")}
      forceRender
    >
      <Form
        form={form}
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 16 }}
        labelAlign="right"
        onFinish={handleSubmit}
        initialValues={{ masine: [{}] }}
      >
        <Flex vertical gap={16} className={style.form}>
          <Form.Item
            name="datumStavljanjaUPromet"
            colon={false}
            label={
              modals.isPosebniPostupak
                ? MandatoryField("EVIDENCIJA_MARKICA.DATUM_PRODAJE")
                : MandatoryField("EVIDENCIJA_MARKICA.DATUM_STAVLJANJA_U_PROMET")
            }
          >
            <DatePicker
              showToday={false}
              placeholder={t("PLACEHOLDERS.IZABERITE_DATUM")}
              format={DATE_FORMAT}
              name="datum-stavljanja-u-promet"
            />
          </Form.Item>

          <Form.Item
            name="kolicina"
            colon={false}
            label={MandatoryField("EVIDENCIJA_MARKICA.KOLICINA")}
          >
            <InputNumber
              placeholder={t("EVIDENCIJA_MARKICA.KOLICINA")}
              name="kolicina"
              style={{ width: "100%" }}
            />
          </Form.Item>
          {showAmabalaza && (
            <Form.Item
              name={"ambalaza"}
              colon={false}
              label={MandatoryField(
                modals.markicaKategorijeId === 1
                  ? `${t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE")} (L)`
                  : `${t("EVIDENCIJA_MARKICA.KOLICINA")} (kg) ${t(
                      "EVIDENCIJA_MARKICA.PO_POJEDINACNOM_PAKOVAJNU"
                    )}`,
                true
              )}
            >
              <InputNumber
                onKeyDown={(event) => clearNonNumericValues(event, ["."])}
                min={0}
                placeholder={
                  modals.markicaKategorijeId === 1
                    ? t("EVIDENCIJA_MARKICA.ZAPREMINA_AMBALAZE_LITRI")
                    : `${t("EVIDENCIJA_MARKICA.KOLICINA_PAKOVANJA_KG")} kg`
                }
                style={{ width: "100%" }}
                name="kolicina-nevidljivih"
                step={0.1}
              />
            </Form.Item>
          )}
        </Flex>
      </Form>
    </Modal>
  );
};
